<!--
  Zwei-Faktor-Authentifizierung (#282).

  Layout note: every row is `flex-col … sm:flex-row`, so the whole panel is a single column on a phone and
  gains its side-by-side arrangement from `sm:` upwards. The recovery-code grid is the one exception and steps
  1 → 2 columns, because a 10-item single column pushes the copy/download buttons off a phone screen — and
  those two buttons are the only thing standing between the reader and a set they can never see again.
-->
<div class="space-y-4">

  @if (loading() && !status()) {
    <p class="text-sm text-gray-500">Wird geladen …</p>
  }

  @if (errorMessage(); as message) {
    <p role="alert" class="rounded-md bg-red-50 p-3 text-sm text-red-700">{{ message }}</p>
  }

  @if (status(); as s) {

    <!-- ---------- status ---------- -->
    <div class="rounded-md border border-gray-200 p-3">
      <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div class="min-w-0 space-y-1">
          <p class="text-sm font-medium text-gray-900">
            @if (s.enabled) {
              <span class="mr-2 inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">Aktiv</span>
              Authenticator-App eingerichtet
            } @else {
              <span class="mr-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">Inaktiv</span>
              Keine Authenticator-App eingerichtet
            }
          </p>
          <p class="text-xs text-gray-500">
            Ein zusätzlicher Code aus einer Authenticator-App (z.&nbsp;B. Microsoft Authenticator, Google
            Authenticator, 1Password) schützt Ihr Konto, falls Ihr Passwort bekannt wird.
          </p>

          @if (s.enabled) {
            <dl class="mt-2 space-y-1 text-xs text-gray-600">
              @if (s.confirmedUtc) {
                <div class="flex flex-col gap-x-1 sm:flex-row">
                  <dt class="font-medium">Eingerichtet am:</dt>
                  <dd>{{ s.confirmedUtc | date: 'dd.MM.yyyy, HH:mm' }} Uhr</dd>
                </div>
              }
              <div class="flex flex-col gap-x-1 sm:flex-row">
                <dt class="font-medium">Zuletzt verwendet:</dt>
                <dd>{{ s.lastUsedUtc ? (s.lastUsedUtc | date: 'dd.MM.yyyy, HH:mm') + ' Uhr' : 'noch nie' }}</dd>
              </div>
              @if (recoveryCodeCount(); as count) {
                <div class="flex flex-col gap-x-1 sm:flex-row">
                  <dt class="font-medium">Wiederherstellungscodes:</dt>
                  <dd [class.text-amber-700]="recoveryCodesLow()"
                      [class.font-semibold]="recoveryCodesLow()">{{ count }} übrig</dd>
                </div>
              }
              <!-- A spent recovery code is a security signal only the account holder can read: either they lost
                   their phone, or somebody else has their printout. A count alone never asks that question. -->
              @if (s.lastRecoveryCodeUsedUtc) {
                <div class="flex flex-col gap-x-1 sm:flex-row">
                  <dt class="font-medium">Letzter Wiederherstellungscode verwendet:</dt>
                  <dd>{{ s.lastRecoveryCodeUsedUtc | date: 'dd.MM.yyyy, HH:mm' }} Uhr</dd>
                </div>
              }
            </dl>
          }
        </div>
      </div>

      <!-- Two sentences, not one with a number in it: "nur noch wenige übrig" printed beside "0 von 10 übrig"
           is simply untrue, and zero is the one state where the warning has to land — there is no way back
           into the account without the phone at that point. -->
      @if (recoveryCodesExhausted()) {
        <p class="mt-3 rounded-md bg-red-50 p-2 text-xs text-red-700">
          Sie haben keine Wiederherstellungscodes mehr. Erzeugen Sie jetzt neue — sonst kommen Sie ohne Ihr
          Gerät nicht mehr in Ihr Konto.
        </p>
      } @else if (recoveryCodesLow()) {
        <p class="mt-3 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
          Es sind nur noch wenige Wiederherstellungscodes übrig. Erzeugen Sie jetzt neue — ohne Codes und ohne
          Ihr Gerät kommen Sie nicht mehr in Ihr Konto.
        </p>
      }
    </div>

    <!-- ---------- company policy ---------- -->
    @if (federatedRequired()) {
      <p class="rounded-md bg-amber-50 p-3 text-xs text-amber-800">
        Ihr Unternehmen verlangt die Anmeldung über einen externen Anbieter (z.&nbsp;B. Microsoft). Eine
        Authenticator-App genügt dafür nicht — bitte wenden Sie sich an Ihre Administration.
      </p>
    } @else if (required()) {
      <p class="rounded-md bg-theme-primary-50 p-3 text-xs text-theme-primary-900">
        Ihr Unternehmen schreibt die Zwei-Faktor-Authentifizierung vor. Ohne eingerichtete Authenticator-App
        können Sie sich nur eingeschränkt anmelden, und die Einrichtung lässt sich nicht wieder entfernen.
      </p>
    }

    <!-- ---------- idle: the actions ---------- -->
    @if (mode() === 'idle') {
      <div class="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        @if (!s.enabled) {
          <button type="button" (click)="startEnrolment()" [disabled]="busy()"
                  class="inline-flex items-center justify-center rounded-md bg-theme-primary-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-theme-primary-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-700 disabled:pointer-events-none disabled:bg-gray-300 disabled:text-gray-600">
            Zwei-Faktor-Authentifizierung einrichten
          </button>
        } @else {
          <button type="button" (click)="beginRegenerate()" [disabled]="busy()"
                  class="inline-flex items-center justify-center rounded-md border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:pointer-events-none disabled:text-gray-400">
            Neue Wiederherstellungscodes erzeugen
          </button>
          <!-- Hidden, not disabled, while the company requires the factor: a control that can never work is
               worse than none, and the sentence beside it says why rather than leaving a dead button to guess at. -->
          @if (s.canDisable) {
            <!-- `text-red-700`, not the `text-red-600` the sibling "Trennen"/"Entfernen" buttons on this page
                 use: red-600 on red-50 measures 4.41:1, and axe counts `font-semibold` (600) as normal weight,
                 so it needs 4.5:1 and fails. red-700 clears it at ~6:1 and is visually the same button. -->
            <button type="button" (click)="beginRemove()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-md bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:pointer-events-none disabled:text-red-400">
              Zwei-Faktor-Authentifizierung entfernen
            </button>
          } @else {
            <p class="self-center text-xs text-gray-500">
              Entfernen ist nicht möglich, weil Ihr Unternehmen die Zwei-Faktor-Authentifizierung vorschreibt.
            </p>
          }
        }
      </div>
    }

    <!-- ---------- enrolling: QR + manual secret ---------- -->
    @if (mode() === 'enrolling' && enrolment(); as e) {
      <div class="space-y-3 rounded-md border border-gray-200 p-3">
        <p class="text-sm font-medium text-gray-900">1. Konto zur Authenticator-App hinzufügen</p>
        <div class="flex flex-col items-start gap-4 sm:flex-row">
          <img [src]="e.qrCode" width="176" height="176"
               alt="QR-Code zum Hinzufügen dieses Kontos zu Ihrer Authenticator-App"
               class="h-44 w-44 shrink-0 rounded-md border border-gray-200 bg-white p-1"/>
          <div class="min-w-0 space-y-2">
            <p class="text-xs text-gray-600">
              Scannen Sie den QR-Code mit Ihrer Authenticator-App. Falls die Kamera nicht verfügbar ist, geben
              Sie diesen Schlüssel manuell ein:
            </p>
            <!-- `break-all` rather than a fixed width: a base32 secret has no spaces, so on a phone it would
                 otherwise push the whole panel into a horizontal scroll. -->
            <code class="block break-all rounded-md bg-gray-100 p-2 font-mono text-xs text-gray-900">{{ e.secret }}</code>
          </div>
        </div>

        <p class="text-sm font-medium text-gray-900">2. Code eingeben</p>
        <p class="text-xs text-gray-600">{{ codePrompt() }}</p>
        <ngx-otp-input [options]="otpOptions" [status]="otpStatus()" [disabled]="busy()"
                       (otpComplete)="submitCode($event)" class="text-sm"/>

        <div class="flex flex-col gap-2 sm:flex-row">
          <button type="button" (click)="cancel()" [disabled]="busy()"
                  class="inline-flex items-center justify-center rounded-md border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:pointer-events-none disabled:text-gray-400">
            Abbrechen
          </button>
          <button type="button" (click)="startEnrolment()" [disabled]="busy()"
                  class="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-semibold text-theme-primary-700 hover:text-theme-primary-900 disabled:pointer-events-none disabled:text-gray-400">
            Neuen QR-Code erzeugen
          </button>
        </div>
      </div>
    }

    <!-- ---------- code required for a regeneration / removal ---------- -->
    @if (mode() === 'regenerating' || mode() === 'removing') {
      <div class="space-y-3 rounded-md border border-gray-200 p-3">
        <p class="text-sm font-medium text-gray-900">Bestätigung erforderlich</p>
        <p class="text-xs text-gray-600">{{ codePrompt() }}</p>
        @if (mode() === 'regenerating') {
          <p class="rounded-md bg-amber-50 p-2 text-xs text-amber-800">
            Ihre bisherigen Wiederherstellungscodes werden dabei ungültig.
          </p>
        }
        @if (usingRecoveryCode()) {
          <!-- A free-text field, not the six-box control: a recovery code is a ~20-character hyphenated
               string, and a numeric six-box entry cannot hold one — it would silently swallow the letters and
               the person would then be told their perfectly good printout is wrong. -->
          <div>
            <label class="block text-sm font-medium leading-6 text-gray-900" for="two-factor-recovery-code">
              Wiederherstellungscode
            </label>
            <!-- ONE listener that assigns first: `[(ngModel)]` desugars into `[ngModel]` + an assigning
                 `(ngModelChange)`, so a second handler beside it would read the PREVIOUS value
                 (cheqme/no-ngmodelchange-before-ngmodel). -->
            <input [ngModel]="recoveryCodeEntry()" (ngModelChange)="recoveryCodeEntry.set($event)"
                   [disabled]="busy()" type="text" id="two-factor-recovery-code" name="two-factor-recovery-code"
                   autocomplete="one-time-code" spellcheck="false" autocapitalize="none" required
                   class="mt-2 block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 font-mono text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6">
          </div>
          <button type="button" (click)="submitRecoveryCode()"
                  [disabled]="busy() || !recoveryCodeEntry().trim()"
                  class="inline-flex w-full items-center justify-center rounded-md bg-theme-primary-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-theme-primary-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-700 disabled:pointer-events-none disabled:bg-gray-300 disabled:text-gray-600 sm:w-auto">
            Bestätigen
          </button>
        } @else {
          <ngx-otp-input [options]="otpOptions" [status]="otpStatus()" [disabled]="busy()"
                         (otpComplete)="submitCode($event)" class="text-sm"/>
        }

        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          <!-- Offered for a removal and a regeneration, never while enrolling (no codes exist before the first
               set is issued) and never on an account whose codes are all spent, where the route would answer
               every entry with a refusal reading "your code is wrong".
               `|| usingRecoveryCode()` keeps the way BACK reachable if the count hits zero mid-flow. -->
          @if (recoveryCodeAccepted() || usingRecoveryCode()) {
            <button type="button" (click)="toggleRecoveryCode(!usingRecoveryCode())" [disabled]="busy()"
                    class="text-left text-sm text-theme-primary-700 hover:text-theme-primary-900 disabled:pointer-events-none disabled:text-gray-400">
              {{ usingRecoveryCode() ? 'Stattdessen Code aus der App verwenden' : 'Wiederherstellungscode verwenden' }}
            </button>
          }
          <button type="button" (click)="cancel()" [disabled]="busy()"
                  class="inline-flex items-center justify-center rounded-md border-2 border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:pointer-events-none disabled:text-gray-400 sm:ml-auto">
            Abbrechen
          </button>
        </div>
      </div>
    }
  }

  <!--
    ---------- the recovery codes, shown once ----------
    Deliberately OUTSIDE the `@if (status())` block above: this view must survive a failed status reload. It is
    the only place these values will ever exist, and losing them to an unrelated network error would be
    unrecoverable for the person reading it.
  -->
  @if (mode() === 'codes' && recoveryCodes(); as codes) {
    <div class="space-y-3 rounded-md border-2 border-amber-300 bg-amber-50 p-3">
      <p class="text-sm font-semibold text-amber-900">
        @if (codesOrigin() === 'confirmed') {
          Zwei-Faktor-Authentifizierung ist aktiv — bitte speichern Sie Ihre Wiederherstellungscodes
        } @else {
          Neue Wiederherstellungscodes
        }
      </p>
      <p role="alert" class="text-xs font-semibold text-amber-900">
        Diese Codes werden Ihnen <span class="underline">nur dieses eine Mal</span> angezeigt. Bewahren Sie sie
        sicher auf — mit einem davon kommen Sie in Ihr Konto, wenn Sie keinen Zugriff auf Ihre Authenticator-App
        haben. Jeder Code funktioniert genau einmal.
      </p>
      @if (codesOrigin() === 'regenerated') {
        <p class="text-xs text-amber-900">Ihre bisherigen Codes sind ab sofort ungültig.</p>
      }

      <ul class="grid grid-cols-1 gap-1 rounded-md bg-white p-3 sm:grid-cols-2">
        @for (code of codes; track code) {
          <li class="select-all font-mono text-sm text-gray-900">{{ code }}</li>
        }
      </ul>

      <div class="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <button type="button" (click)="copyCodes()"
                class="inline-flex items-center justify-center rounded-md border-2 border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-900 hover:bg-amber-100">
          {{ copied() ? 'Kopiert ✓' : 'Codes kopieren' }}
        </button>
        <button type="button" (click)="downloadCodes()"
                class="inline-flex items-center justify-center rounded-md border-2 border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-900 hover:bg-amber-100">
          Als Datei herunterladen
        </button>
        <button type="button" (click)="acknowledgeCodes()"
                class="inline-flex items-center justify-center rounded-md bg-theme-primary-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-theme-primary-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-700">
          Ich habe die Codes gespeichert
        </button>
      </div>
    </div>
  }
</div>
